import React, { useEffect, useState } from "react";
import {
    Row,
    Col,
    Button,
    Form,
} from "antd";
import {
    decodeParamString,
} from "../const/const_functions";
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const StripeSetupForm = ({ onSubmit, paymentElementOptions, clientSecret, setupStatus, maxWidth = false }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [form] = Form.useForm();

    const [errorMessage, setErrorMessage] = useState();
    const [loading, setLoading] = useState(false);

    const handleError = async (error) => {
        setLoading(false);
        setErrorMessage(error.message);
    }

    const handleSubmit = async (values) => {
        if (!stripe || !elements) {
            return;
        }

        setLoading(true);
        setErrorMessage(null);

        try {
            // STEP 1: ALWAYS call elements.submit() first
            const { error: submitError } = await elements.submit();
            if (submitError) {
                handleError(submitError);
                return;
            }

            // STEP 2: Then confirm the setup intent
            if (clientSecret && setupStatus === 'requires_action') {
                const { error: nextError, setupIntent } = await stripe.confirmSetup({
                    elements,
                    clientSecret,
                    confirmParams: {
                        return_url: window.location.href,
                    },
                    redirect: 'if_required',
                });

                if (nextError) {
                    handleError(nextError);
                    return;
                }

                onSubmit(setupIntent.id, { setupIntent });
                return;
            }

            // Regular confirmation
            const { error: confirmError, setupIntent } = await stripe.confirmSetup({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required',
            });

            if (confirmError) {
                handleError(confirmError);
                return;
            }

            onSubmit(setupIntent.id, { setupIntent });

        } catch (error) {
            console.error('Setup confirmation error:', error);
            handleError(error);
        }
    };

    // Reset loading state when setup completes
    useEffect(() => {
        if (setupStatus !== 'initial' && setupStatus !== 'requires_action') {
            setLoading(false);
        }
    }, [setupStatus]);

    return (
        <Row justify="center" style={{ paddingTop: 0 }}>
            <Col xl={maxWidth ? 24 : 12} lg={maxWidth ? 24 : 18} md={maxWidth ? 24 : 24}>
                <Form form={form} onFinish={handleSubmit}>
                    <div style={{ borderRadius: '6px', padding: '1px' }}>
                        <PaymentElement options={paymentElementOptions} />
                        <Button
                            type="primary"
                            htmlType="submit"
                            disabled={!stripe || !elements || loading || setupStatus === 'succeeded'}
                            loading={loading}
                            style={{
                                backgroundColor: '#0570de',
                                color: 'white',
                                width: '100%',
                                fontSize: "14px",
                                fontWeight: 'bold',
                                margin: '10px 0'
                            }}
                        >
                            {loading ? 'Processing...' :
                                setupStatus === 'requires_action' ? 'Action Required: Please Click Again' :
                                    'Register Card'}
                        </Button>
                    </div>

                    {errorMessage && (
                        <div style={{ color: '#ff4d4f', marginTop: '10px', textAlign: 'center' }}>
                            {errorMessage}
                        </div>
                    )}

                    {setupStatus === 'succeeded' && (
                        <div style={{ color: '#52c41a', marginTop: '10px', textAlign: 'center' }}>
                            Card registered successfully!
                        </div>
                    )}
                </Form>
            </Col>
        </Row>
    );
}

const StripeCardRegister = ({
    clientToken,
    paymentCurrency = 'CAD',
    registerTheCard,
    clientSecret,
    setupStatus,
    maxWidth
}) => {
    const [stripePromise, setStripePromise] = useState(null);

    useEffect(() => {
        if (clientToken) {
            const stripePromise = loadStripe(decodeParamString(clientToken));
            setStripePromise(stripePromise);
        }
    }, [clientToken]);

    const formOptions = {
        mode: 'setup',
        currency: paymentCurrency.toLowerCase(),
        paymentMethodCreation: 'manual',
        paymentMethodTypes: ['card'],
        appearance: {
            theme: 'flat',
            // Optional appearance customization
            // variables: {
            //     colorPrimary: '#0570de',
            //     colorBackground: '#ffffff',
            //     colorText: '#30313d',
            //     colorDanger: '#df1b41',
            //     fontFamily: 'Ideal Sans, system-ui, sans-serif',
            //     spacingUnit: '2px',
            //     borderRadius: '4px',
            // }
        }
    };

    const paymentElementOptions = {
        layout: {
            type: 'tabs',
            defaultCollapsed: false
        },
        fields: {
            billingDetails: {
                address: {
                    mode: 'minimal',
                    country: 'auto',
                    postalCode: 'auto' // Hide postal code field
                }
            }
        }
    };

    const handleSetupComplete = async (setupIntentId, nextAction) => {
        registerTheCard({
            setupIntentId,
            paymentMethod: nextAction?.setupIntent?.payment_method
        }, false, nextAction);
    }

    return (
        <Elements stripe={stripePromise} options={formOptions}>
            <StripeSetupForm
                onSubmit={handleSetupComplete}
                paymentElementOptions={paymentElementOptions}
                clientSecret={clientSecret}
                setupStatus={setupStatus}
                maxWidth={maxWidth}
            />
        </Elements>
    )
}

export default StripeCardRegister;